import { Vector2 } from "../math/vector2";
import { Ball } from "./ball";
import { IBallConfig, IPhysicsConfig,ITableConfig, IVector2 } from "../types/game-config-type";
import { GameConfig } from "../config/game-config";

const physicsConfig: IPhysicsConfig = GameConfig.physics;
const ballConfig: IBallConfig = GameConfig.ball;
const tableConfig: ITableConfig = GameConfig.table;
const gameSize: IVector2 = GameConfig.gameSize;

/**
 * This contains the lower level table collision game events.
 */
export class TableEvents {

  // loss of power when the cushion is hit
  protected static CUSHION_LOSS: number = 0.95;

  constructor() {
  }

  /**
   * Determines if the ball is inside or outside of the table border.
   * 
   * @param position the position of the ball
   * @returns Returns "true" if the ball is outside of the border and "true" if not.
   */
  public isBallPositionOutsideBorder(position: Vector2): boolean {
    let insideBorder: boolean = !this.isBallPosOutsideTopBorder(position);
    insideBorder = insideBorder && !this.isBallPosOutsideLeftBorder(position);
    insideBorder = insideBorder && !this.isBallPosOutsideRightBorder(position);
    insideBorder = insideBorder && !this.isBallPosOutsideBottomBorder(position); 
    return insideBorder;
  }   

  /**
   * This is executed when a ball hits the rail (cushion).
   * 
   * @param i - the ball index
   * @param balls - the array of balls
   * @returns  Returns "true" if a ball hits a rail (cushion) and "false" if not. 
   */
  public ballRailEvent(i: number, balls: Ball[]): boolean {
    let ball: Ball = balls[i];
    let railsCollisions: boolean = false;

    if (this.isBallPosOutsideTopBorder(ball.nextPosition)) {
        this.handleCollisionWithTopCushion(ball);
        railsCollisions = true;
    }
    if (this.isBallPosOutsideLeftBorder(ball.nextPosition)) {
        this.handleCollisionWithLeftCushion(ball);
        railsCollisions = true;
    }
    if (this.isBallPosOutsideRightBorder(ball.nextPosition)) {
        this.handleCollisionWithRightCushion(ball);
        railsCollisions = true;
    }
    if (this.isBallPosOutsideBottomBorder(ball.nextPosition)) {
        this.handleCollisionWithBottomCushion(ball);
        railsCollisions = true;
    }

    return railsCollisions
  }

  /**
   * This determines when two balls collide.
   * If two balls have collided this executes the collision.
   * 
   * @param first - the first ball
   * @param second - the second ball
   * @returns Returns "true" if the balls have collided and "false" if not. 
   */
  public ballBallEvent(first: Ball, second: Ball): boolean {
    
    if (!first.visible || !second.visible) {
      return false;
    }

    // This is a safety check incase the balls are touching when they are in the rack.
    if (!first.moving && !second.moving) {
      return false;
    }

    // find a normal vector
    const n: Vector2 = second.position.subtract(first.position);

    // find distance
    const dist: number = n.length;

    // Determine if the two balls have collided.
    if (dist >= ballConfig.diameter) {
      return false;
    }

    // Find minimum translation distance.
    const mtd = n.mult((ballConfig.diameter - dist) / dist);

    // Push-pull balls apart.
    first.position = first.position.subtract(mtd.mult(0.5));
    second.position = second.position.add(mtd.mult(0.5));

    // Find unit normal vector.
    const ni: Vector2 = second.position.subtract(first.position);
    const un = ni.mult(1 / ni.length);

    // Find unit tangent vector.
    const ut = new Vector2(-un.y, un.x);

    // Project velocities onto the unit normal and unit tangent vectors.
    const v1n: number = un.dot(first.velocity);
    const v1t: number = ut.dot(first.velocity);
    const v2n: number = un.dot(second.velocity);
    const v2t: number = ut.dot(second.velocity);

    // Velocity due to rotation around x and y.
    const xVelocity1: number = first.rotation.x * ballConfig.diameter / 2 * physicsConfig.rotationToVelocity
    const yVelocity1: number = first.rotation.y * ballConfig.diameter / 2 * physicsConfig.rotationToVelocity
    const xVelocity2: number = second.rotation.x * ballConfig.diameter / 2 * physicsConfig.rotationToVelocity
    const yVelocity2: number = second.rotation.y * ballConfig.diameter / 2 * physicsConfig.rotationToVelocity
    
    // Convert the scalar normal and tangential velocities into vectors.
    const v1nTag: Vector2 = un.mult(-Math.abs(v2n) + xVelocity1);
    const v1tTag: Vector2 = ut.mult(v1t + yVelocity1);
    const v2nTag: Vector2 = un.mult(Math.abs(Math.abs(v1n) + xVelocity2));
    const v2tTag: Vector2 = ut.mult(v2t + yVelocity2);
    
    // Update velocities (sum velocities from hit and rotation).
    first.velocity = v1nTag.add(v1tTag);
    second.velocity = v2nTag.add(v2tTag)

    // Friction effect on velocity.
    first.velocity = first.velocity.mult(1 - physicsConfig.collisionLoss);
    second.velocity = second.velocity.mult(1 - physicsConfig.collisionLoss);

    // Update rotations
    first.rotation = { x: 0, y: 0, z: 0 }
    second.rotation = { x: 0, y: 0, z: 0 }

    return true;
  }

  /**
   * Determines if the position is inside of the pocket.
   *
   * @param position - the position
   * @returns - return "true" if the position is inside the packet "false" if not
   */
  public isInsidePocket(position: Vector2): boolean {
    return tableConfig.pocketsPositions.some(
      (pocketPos: IVector2) =>
        position.distFrom(pocketPos as Vector2) <= tableConfig.pocketRadius
    );
  }

  /**
   * Determines if the ball is inside the table playing area.
   *
   * @param position - the position
   * @returns - return "true" if the position is on the table
   * playing area and return "false" if not
   */
  public isInsideTableBoundaries(position: Vector2): boolean {
    let onTable = !this.isInsidePocket(position);
    let insideBorder = this.isBallPositionOutsideBorder(position);
    return onTable && insideBorder;
  }

  private isBallPosOutsideTopBorder(position: Vector2): boolean {
    const topBallEdge: number = position.y - ballConfig.diameter / 2;
    return topBallEdge <= tableConfig.cushionWidth;
  }

  private isBallPosOutsideLeftBorder(position: Vector2): boolean {
    const leftBallEdge: number = position.x - ballConfig.diameter / 2;
    return (leftBallEdge <= tableConfig.cushionWidth + GameConfig.pottedBallsWidth);
  }

  private isBallPosOutsideRightBorder(position: Vector2): boolean {
    const rightBallEdge: number = position.x + ballConfig.diameter / 2;
    return rightBallEdge >= gameSize.x - tableConfig.cushionWidth;
  }

  private isBallPosOutsideBottomBorder(position: Vector2): boolean {
    const bottomBallEdge: number = position.y + ballConfig.diameter / 2;
    return bottomBallEdge >= gameSize.y - tableConfig.cushionWidth;
  }

  private handleCollisionWithTopCushion(ball: Ball): void {
    ball.position = ball.position.addY(
      (tableConfig.cushionWidth) - ball.position.y + ballConfig.diameter / 2
    );
    ball.velocity = new Vector2(ball.velocity.x, -ball.velocity.y);
    ball.velocity = ball.velocity.mult(TableEvents.CUSHION_LOSS);
  }

  private handleCollisionWithLeftCushion(ball: Ball): void {
    ball.position = ball.position.addX(tableConfig.cushionWidth +
      GameConfig.pottedBallsWidth - ball.position.x + ballConfig.diameter / 2);
    ball.velocity = new Vector2(-ball.velocity.x, ball.velocity.y);
    ball.velocity = ball.velocity.mult(TableEvents.CUSHION_LOSS);
  }

  private handleCollisionWithRightCushion(ball: Ball): void {
    ball.position = ball.position.addX(gameSize.x -
      tableConfig.cushionWidth - ball.position.x - ballConfig.diameter / 2);
    ball.velocity = new Vector2(-ball.velocity.x, ball.velocity.y);
    ball.velocity = ball.velocity.mult(TableEvents.CUSHION_LOSS);
  }

  private handleCollisionWithBottomCushion(ball: Ball): void {
    ball.position = ball.position.addY(gameSize.y - (tableConfig.cushionWidth) -
        ball.position.y - ballConfig.diameter / 2);
    ball.velocity = new Vector2(ball.velocity.x, -ball.velocity.y);
    ball.velocity = ball.velocity.mult(TableEvents.CUSHION_LOSS);
  }
}